<template>
  <span>
    <portal to="global-modal-portal" v-if="inactivePpaRejectActive" :disabled="!usePortal" :order="portalOrder">
      <b-modal :active.sync="inactivePpaRejectActive" scroll="keep" width="640px" :has-modal-card="true" :canCancel="true" >
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ modalHeader }}</p>
              <button class="button is-small" @click="closePpaModal" type="button" aria-label="Close">
                <span class="icon"><i class="fas fa-times"></i></span>
              </button>
          </header>
          <section class="modal-card-body">
            <div class="notification is-flex is-warning">
              <span><i class="fas fa-exclamation-circle is-warning fa-2x"></i></span>
                <span class="ml-10">
                  {{ modalBody }}
                </span>
            </div>
            <br/>
            <div class="columns is-multiline" v-if="leadStatusValue && !inventoryItem">
              <div class="column is-12">
                <label class="label">Lead status</label>
                <div class="field">
                  {{leadStatusValue}}
                </div>
              </div>
            </div>
            <div class="column is-12 mt-10" v-if="inventoryItem && leadStatusValue">
              <customer-info-control
                :key="inventoryItem.id"
                :runAutomatically="true"
                :customerNameLabel="'Purchased From'"
                :customerId="inventoryItem.purchasedFromId"
                :customerMobile="inventoryItem.purchasedFromContactMobile"
                :customerEmail="inventoryItem.purchasedFromContactEmail"
                :inventoryId="inventoryItem.id"
                v-on:lead-status="setLeadStatusValue"
                v-on:close="closePpaModal">
              </customer-info-control>
            </div>
          </section>
          <!--
            ⚠ The SAFE answer is the primary button, and it comes first (meeting GMT20261003). "Yes" used to be the
            green primary, and staff clicked it without reading - rejecting PPAs mid-conversion. Rejecting is the
            danger action and says what it does; Esc and the close button still mean "keep".
          -->
          <footer class="modal-card-foot">
            <div class="columns" style="width: 100%">
              <div class="column is-12">
                <button class="button is-primary" ref="keepPpaButton" type="button" @click="closePpaModal">No, keep the PPA</button>
                <button class="button is-danger" type="button" :class="{'is-loading': isSaving }" @click="showClosePpaControl()">Yes, reject the PPA</button>
              </div>
            </div>
          </footer>
        </div>
      </b-modal>
    </portal>
  </span>
</template>

<script>
import utilitiesMixin from '@/mixins/generic/utilities'
import { createNamespacedHelpers } from 'vuex'
import dealerPeakService from '@/services/dealerPeakService'
import CustomerInfoControl from '@/components/generic/CustomerInfoControl'

export default {
  mixins: [utilitiesMixin],
  inject: {
  },
  components: {
    'customer-info-control': CustomerInfoControl
  },
  name: 'PpaInactiveLeadCheckControl',
  props: {
    inventoryItem: {
      type: Object,
      default: null
    },
    inventoryLeadsCheckRequested: {
      type: Boolean,
      default: false
    },
    validPpaStatus: {
      type: Boolean,
      default: false
    },
    hasClosePpaAccess: {
      type: Boolean,
      default: false
    },
    usePortal: {
      type: Boolean,
      default: true
    },
    portalOrder: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      isSaving: false,
      instanceName: this.$uuid.v4(),
      isCustomerSyncing: false,
      customerSyncSuccessful: false,
      inventoryCustomerLeadCheckId: null,
      currentPurchaser: null,
      leadStatusValue: '',
      inactivePpaRejectActive: false,
      currentInventory: null,
      customer: null,
    }
  },
  methods: {
    checkInventoryLeads: function () {
      if (this.currentInventory && this.currentInventory.type === 'ForPpaAppraisal' && this.validPpaStatus === true && this.hasClosePpaAccess === true) {
        // sync customer then check latest lead status
        this.syncCustomerCheckLeadStatus()
      }
    },
    syncCustomerCheckLeadStatus: async function () {
      let inventoryId = this.currentInventory.id
      if (!this.inventoryCustomerLeadCheckId || this.inventoryCustomerLeadCheckId !== inventoryId) {
        this.inventoryCustomerLeadCheckId = this.currentInventory.id
        await this.syncAndLoadPurchaser()
      }
    },
    syncAndLoadPurchaser: async function () {
      if (!this.currentInventory) {
        console.warn('Cannot sync purchaser: inventory item is null')
        return
      }
      this.customerSyncSuccessful = false
      this.isCustomerSyncing = true
      let model = {
        number: this.unmask(this.currentInventory.purchasedFromContactMobile),
        email: this.currentInventory.purchasedFromContactEmail,
        customerType: 'Lead',
        customerId: this.currentInventory.purchasedFromId,
        source: 'LIVE',
        clearExistingLeads: true,
        openLeadsOnly: true
      }
      try {
        let response = await dealerPeakService.syncCustomer(model)
        if (response.data !== null) {
          this.customer = response.bestLiveMatch
          this.setLeadStatus()
          this.customerSyncSuccessful = true
        } else {
          this.failedToast('Unable to sync customer data. Please try again.')
        }
      } catch (err) {
        this.failedToast('Error when resyncing customer: ' + err)
      } finally {
        this.isCustomerSyncing = false
      }
    },
    getStatusCategory: function (status) {
      const ACTIVE_STATUSES = [
        'New Lead',
        'BDC ONLY',
        'No Contact Made',
        'Awaiting a reply from US',
        'Consignment / Selling their car PROSPECT',
        'Consignment / Selling their car PRSOSPECT',
        'Contact Made - No Appt. Set',
        'Appointment Set',
        'Appt. - No Show',
        'Showroom In Store Visit',
        'HOT'
      ]
      const FUTURE_STATUSES = [
        'Delayed Follow UP',
        '3 to 6 Months Out from Ready',
        'Wait List',
        'Car on Consignment',
        'Ordered/Allocated',
        'Ongoing Follow Up',
        'Need to Locate the Vehicle',
        'Fleet / Wholesaler / Rental'
      ]
      // ⚠ A sold lead is a sale in progress or completed - never a lost customer, so never a reason to offer rejecting
      // the PPA. "Sold - Deposit Collected" fell through to Inactive, and the prompt it raised rejected PPAs during their
      // conversion to trade stock (meeting GMT20261003). Every other status is classified exactly as before.
      const SOLD_STATUSES = [
        'Sold - Deposit Collected',
        'Sold - Sale Completed'
      ]
      if (!status) return 'Inactive'
      const statusValue = status.trim()
      if (ACTIVE_STATUSES.includes(statusValue)) return 'Active'
      if (FUTURE_STATUSES.includes(statusValue)) return 'Future'
      if (SOLD_STATUSES.includes(statusValue)) return 'Sold'
      return 'Inactive'
    },
    setLeadStatus: function () {
      if (this.customer && this.customer.customerLeads && this.currentInventory && this.currentInventory.id && this.inventoryCustomerLeadCheckId && this.inventoryCustomerLeadCheckId === this.currentInventory.id) {
        this.inventoryCustomerLeadCheckId = null
        const leadWithMatchingId = this.customer?.customerLeads?.find(lead => lead.trades?.some(trade => trade.inventory?.id === this.currentInventory.id))
        if (leadWithMatchingId) {
          this.leadStatusValue = leadWithMatchingId.leadStatus
          let statusCategory = this.getStatusCategory(this.leadStatusValue)
          if (statusCategory === 'Inactive' && this.leadStatusValue) {
            this.inactivePpaRejectActive = true
          }
        }
      }      
    },
    showClosePpaControl: function () {
      this.$emit('show-close-ppa-control')
      this.inactivePpaRejectActive = false
    },
    closePpaModal: function () {
      this.inactivePpaRejectActive = false
    },
    setLeadStatusValue: function (crmModel) {
      if (crmModel.status && crmModel.status !== '-') {
        this.leadStatusValue = crmModel.status
      }
    }
  },
  watch: {
    inventoryItem: function (newValue, oldValue) {
      this.currentInventory = newValue
    },
    inventoryLeadsCheckRequested: function (newValue, oldValue) {
      if (newValue === true) {
        this.checkInventoryLeads()
      }
    },
    //Focus the safe answer, so a reflexive Enter keeps the PPA rather than starting its rejection.
    inactivePpaRejectActive: function (newValue) {
      if (newValue !== true) return
      this.$nextTick(() => {
        const keep = this.$refs.keepPpaButton
        if (keep && typeof keep.focus === 'function') keep.focus()
      })
    }
  },
  computed: {
    modalHeader: function () {
      if (this.leadStatusValue) {
        return 'Inactive lead (' +  this.leadStatusValue + ')'
      }
      return 'Inactive lead'
    },
    modalBody: function () {
      if (this.inventoryItem && this.inventoryItem.name) {
        
        if (this.vehicleInterests && this.vehicleInterests.length === 1 ) {
          return 'The CRM status for this PPA may be inactive, would you like to reject this PPA for ' + this.inventoryItem.name + ' linked with a trade for ' + this.vehicleInterests[0].inventory.listName +'.'
        } else if (this.vehicleInterests && this.vehicleInterests.length > 1 ) {
          return 'The CRM status for this PPA may be inactive, would you like to reject this PPA for ' + this.inventoryItem.name + ' linked with a trade for the vehicles listed below.'
        } else {
          return 'The CRM status for this PPA may be inactive, would you like to reject this PPA for ' + this.inventoryItem.name + '.'
        }
      }
      return 'The CRM status for this PPA may be inactive, would you like to reject this PPA?'
    },
    vehicleInterests: function () {
      if (!this.customer || !this.customer.customerLeads || !this.currentInventory) return []

      let inventoryId = this.currentInventory.id
      const leadWithMatchingId = this.customer.customerLeads.find(lead => lead.trades?.some(trade => trade.inventory?.id === inventoryId))
      if (!leadWithMatchingId) return []

      const leadsWithInventory = leadWithMatchingId.vehicleInterests.filter(vehicleInterest => vehicleInterest.inventory != null)
      return leadsWithInventory
    }
  }
}
</script>

<style scoped>
  .modal-card-body {
    position:relative;
    max-height: calc(70vh - 10px) !important;
    height: 700px !important;
    max-width: 100% !important;
    min-width: 100% !important;
    overflow-y: auto !important;
    width: 820px !important;
  }
</style>
